// Initial game state factory and helpers

export const WHITE_KING_ONLY_FEN = '8/8/8/8/8/8/8/4K3 w - - 0 1';

/**
 * Generate a random seed for augment offers.
 * Uses crypto.getRandomValues when available, falls back to Math.random.
 */
export function generateAugmentSeed() {
  let augSeed = 0;
  try {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      const arr = new Uint32Array(1);
      window.crypto.getRandomValues(arr);
      augSeed = (arr[0] >>> 0);
    } else {
      augSeed = (Math.floor(Math.random() * 0xffffffff) >>> 0);
    }
  } catch (_) {
    augSeed = (Math.floor(Math.random() * 0xffffffff) >>> 0);
  }
  return augSeed;
}

/**
 * Create the base initial state object (without game instance or computed values).
 * The Game component must populate: game, uiPosition, shop, augShopOffers
 */
export function createBaseState(augSeed, startingFen = WHITE_KING_ONLY_FEN) {
  return {
    fen: startingFen,
    uiPosition: {},
    started: null,
    phase: 'prep',
    roundNumber: 1,
    money: 5,
    game: null, // Must be set by caller
    inventory: { p: 0, n: 0, b: 0, r: 0, q: 0 },
    pendingPlacementPiece: null,
    bench: { wP: 0, wN: 0, wB: 0, wR: 0, wQ: 0 },
    benchIds: { wP: [], wN: [], wB: [], wR: [], wQ: [] },
    nextBenchId: 1,
    benchNonce: 0,
    shop: [],
    currentSpareOrigin: null,
    currentSpareType: null,
    whiteSetupSnapshotFen: null,
    fenPanelOpen: false,
    showResultOverlay: false,
    resultSummary: null,
    overlayFade: false,
    showShopHint: true,
    showBenchHint: true,
    showStartGlow: false,
    startGlowAllowed: true,
    benchGlowAllowed: true,
    shopGlowActive: true,
    benchGlowActive: false,
    enemyPreviewGlowActive: false,
    augmentsGlowActive: false,
    sellStagedPiece: null,
    sellPanelCollapsed: true,
    evalInfo: null,
    autoPreviewEnabled: false,
    startingWhiteValue: null,
    oppBtnHover: false,
    oppBtnActive: false,
    // Enemy preview controls
    showEnemyPreview: false,
    prepSetupFen: null,
    // Augments State
    augShopOffers: [],
    augRollNonce: 0,
    augSeed,
    augmentsByUnitId: {},
    augmentRuntime: {},
    augmentGoldBattle: 0,
    pendingBenchRewards: { wP: 0, wN: 0, wB: 0, wR: 0, wQ: 0 },
    unitIdBySquare: {},
    prepUnitIdBySquare: {},
    unitIdByBenchId: {},
    nextUnitId: 1,
    kingUnitId: null,
    augDockStaged: [null, null]
  };
}

/**
 * Add the starting king to the state.
 * Returns an updated state with prepUnitIdBySquare, kingUnitId, and nextUnitId set.
 */
export function addStartingKing(baseState) {
  try {
    const uid = 'u' + ((baseState.nextUnitId || 1));
    const prepMap = { ...(baseState.prepUnitIdBySquare || {}), e1: uid };
    return {
      ...baseState,
      prepUnitIdBySquare: prepMap,
      kingUnitId: uid,
      nextUnitId: (baseState.nextUnitId || 1) + 1
    };
  } catch (_) {
    return baseState;
  }
}

